import React, { useEffect, useRef, useState } from "react";
import { useThree } from "@react-three/fiber";
import * as THREE from "three";
import gsap from "gsap";
import { useSceneNodes } from "./SecondaryScene";
import CameraManager from "../manager/CameraManager";
import useFlowStore from "../store/flowStore";
import useCursorStore from "../store/cursorStore";
import useIntersectingClicks from "../../hooks/useIntersectingClicks";
import useSeamlessLoopAudio from "../../hooks/useSeemlessLoopAudio";
import { audios, subtitles } from "../../assets/assets";

export default function TuyauScene({ interactUIRef }) {
  const { tuyau } = useSceneNodes();
  const liquids = useRef([]);
  const tuyauMap = useRef({});
  const tuyauxRemplis = useRef(new Set());
  const [levierDisabled, setLevierDisabled] = useState(false);
  const { nextStep, playAudioFile, setShowInteractUI } = useFlowStore();
  const { setCursorScale, setCursorInteractive } = useCursorStore.getState();
  const [encouragementPlayed, setEncouragementPlayed] = useState(false);
  const [soundPlaying, setSoundPlaying] = useState(false);

  useSeamlessLoopAudio(
    audios.fond_sonor_machine,
    0.2,
    soundPlaying
  );

  useEffect(() => {
    if (!tuyau) return;

    const { currentStep, flowSteps } = useFlowStore.getState();
    const step = flowSteps[currentStep];
    if (step?.label === "Carburant") {
      setCursorInteractive(true);
      setShowInteractUI(true);
      setSoundPlaying(true);

      setTimeout(() => {
        CameraManager.enableMouseLook();
      }, 1000);
    }
  }, [tuyau, useFlowStore((s) => s.currentStep)]);

  useEffect(() => {
    if (!tuyau) return;

    const Levier = tuyau.getObjectByName("levier_for_bake");

    // Mapping hitbox → pièce
    const hitboxToPieceMap = {
      hitbox_pipe_jaune: "piece_move_3",
      hitbox_pipe_bleu: "piece_move_2",
      hitbox_pipe_rose: "piece_move_1",
    };

    // Mapping manivelle → pièce
    const manivelles = {
      manivelle_bleu: "piece_move_2",
      manivelle_jaune: "piece_move_3",
      manivelle_rose: "piece_move_1",
    };

    // Pièce → manivelle (pour accès rapide)
    const pieceToManivelleMap = {};
    Object.entries(manivelles).forEach(([maniName, pieceName]) => {
      pieceToManivelleMap[pieceName] = maniName;
    });

    const pieceColorMap = {
      piece_move_1: 0xf24fb6,
      piece_move_2: 0x78b6c8,
      piece_move_3: 0xe8be00,
    };

    const pieceLiquidesMap = {
      piece_move_1: ["liquide_2"],
      piece_move_2: ["liquide_3"],
      piece_move_3: ["liquide_1"],
    };

    // ----------- PIECES ET ORDRE D'ÉCOULEMENT (GAUCHE à DROITE) -----------
    // Ordre inversé : d'abord piece_move_3, puis piece_move_2, puis piece_move_1
    const pieces = [
      { name: "piece_move_3", axis: "x" },
      { name: "piece_move_2", axis: "x" },
      { name: "piece_move_1", axis: "x" },
    ];

    // --- Définir les positions cibles en radians (PI = 180°)
    const pieceTargetRotations = {
      piece_move_1: [Math.PI / 2], // 90°
      piece_move_2: [0, Math.PI], // 0° et 180°
      piece_move_3: [Math.PI / 2, (3 * Math.PI) / 2], // 90° et 270°
    };

    // Tolérance d'alignement
    const ROT_TOLERANCE = 0.1;

    // Vérifie si la rotation X d'une pièce est proche de la cible
    const isPieceAtTargetRotation = (piece, targets, axis = "x") => {
      let rot = piece.rotation[axis];
      rot = ((rot % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
      // Vérifie si la rotation est proche d'au moins une des positions cibles
      return targets.some((targetRad) => {
        targetRad = ((targetRad % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
        let diff = Math.abs(rot - targetRad);
        diff = Math.min(diff, 2 * Math.PI - diff);
        return diff < ROT_TOLERANCE;
      });
    };

    // ----------- UTILITAIRE DE ROTATION -----------
    // Ajoute une promesse pour permettre d'attendre plusieurs animations en parallèle
    const rotateLocally = (obj, axisName, angle, duration = 1) => {
      return new Promise((resolve) => {
        const axis = new THREE.Vector3(
          axisName === "x" ? 1 : 0,
          axisName === "y" ? 1 : 0,
          axisName === "z" ? 1 : 0
        );
        let totalRotated = 0;
        const state = { t: 0 };

        gsap.to(state, {
          t: angle,
          duration,
          ease: "power2.inOut",
          onUpdate: () => {
            const delta = state.t - totalRotated;
            obj.rotateOnAxis(axis, delta);
            totalRotated = state.t;
          },
          onComplete: resolve,
        });
      });
    };

    // ----------- INITIALISATION DES PIECES -----------
    pieces.forEach(({ name, axis }) => {
      const piece = tuyau.getObjectByName(name);
      if (!piece) {
        console.warn(`❗ Pièce ${name} introuvable`);
        return;
      }

      piece.material = piece.material.clone();
      piece.material.isClonedForGlow = true;
      piece.material.emissive = new THREE.Color(pieceColorMap[name]);
      piece.material.emissiveIntensity = 1.5;

      // Désactive interaction directe
      piece.userData.isClickable = false;
      piece.userData.onPointerOver = undefined;
      piece.userData.onPointerOut = undefined;
      piece.userData.onClick = undefined;

      piece.userData.handleHitboxClick = async () => {
        // Son à chaque rotation
        const turnAudio = new Audio(audios.pipe_tourner);
        turnAudio.play().catch(console.warn);

        const maniName = pieceToManivelleMap[name];
        const direction = name === "piece_move_1" ? -1 : 1;
        const pieceRotationAngle = (direction * Math.PI) / 2;
        const manivelleDuration = 0.8;

        // Fonction de log après rotation
        const logRotation = () => {
          let rot = piece.rotation[axis];
          rot = ((rot % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
          const deg = (rot * 180) / Math.PI;
          console.log(
            `🔄 [${name}] Rotation X = ${rot.toFixed(3)} rad (${deg.toFixed(
              1
            )}°)`
          );
        };

        if (maniName) {
          const manivelle = tuyau.getObjectByName(maniName);
          if (manivelle) {
            const manivelleAngle =
              name === "piece_move_1" ? -Math.PI / 2 : Math.PI / 2;
            await Promise.all([
              rotateLocally(manivelle, "x", manivelleAngle, manivelleDuration),
              rotateLocally(piece, axis, pieceRotationAngle, manivelleDuration)
            ]);
            logRotation();
          } else {
            await rotateLocally(piece, axis, pieceRotationAngle, 1);
            logRotation();
          }
        } else {
          await rotateLocally(piece, axis, pieceRotationAngle, 1);
          logRotation();
        }

        if (!encouragementPlayed) {
          const piecesNames = ["piece_move_1", "piece_move_2", "piece_move_3"];
          let atLeastOneCorrect = false;
          for (const nameToCheck of piecesNames) {
            const p = tuyau.getObjectByName(nameToCheck);
            if (
              p &&
              isPieceAtTargetRotation(p, pieceTargetRotations[nameToCheck], "x")
            ) {
              atLeastOneCorrect = true;
              break;
            }
          }
          if (atLeastOneCorrect) {
            setEncouragementPlayed(true);
            playAudioFile(
              audios.encouragement_emotion,
              subtitles.encouragement_emotion
            );
          }
        }
      };
    });

    // ----------- HITBOXES DES PIECES -----------
    liquids.current = [];
    tuyauMap.current = {};

    Object.entries(hitboxToPieceMap).forEach(([hitboxName, pieceName]) => {
      const hitbox = tuyau.getObjectByName(hitboxName);
      const piece = tuyau.getObjectByName(pieceName);

      if (!hitbox || !piece) return;

      hitbox.visible = false;
      hitbox.frustumCulled = false;

      hitbox.userData.onPointerOver = () => setCursorScale(3.5);
      hitbox.userData.onPointerOut = () => setCursorScale(1);

      hitbox.userData.isClickable = true;
      hitbox.userData.onClick = piece.userData.handleHitboxClick;
    });

    // ----------- GESTION DU LEVIER (va-et-vient sur Z et validation) -----------
    const hitboxLevier = tuyau.getObjectByName("hitbox_levier");
    if (hitboxLevier && Levier) {
      hitboxLevier.visible = false;
      hitboxLevier.frustumCulled = false;

      hitboxLevier.userData.onPointerOver = () => {
        if (!levierDisabled) setCursorScale(3.5);
      };
      hitboxLevier.userData.onPointerOut = () => {
        if (!levierDisabled) setCursorScale(1);
      };

      hitboxLevier.userData.isClickable = true;
      hitboxLevier.userData.onClick = () => {
        const levierAudio = new Audio(audios.levier);
        levierAudio.play().catch(console.warn);

        if (levierDisabled) return;

        setLevierDisabled(true);
        gsap.to(Levier.rotation, {
          z: Levier.rotation.z - Math.PI / 2,
          duration: 0.35,
          ease: "power2.inOut",
          onComplete: () => {
            gsap.to(Levier.rotation, {
              z: Levier.rotation.z + Math.PI / 2,
              duration: 0.35,
              ease: "power2.inOut",
              onComplete: () => {
                let ready = true;
                pieces.forEach(({ name, axis }) => {
                  const piece = tuyau.getObjectByName(name);
                  if (
                    !piece ||
                    !isPieceAtTargetRotation(
                      piece,
                      pieceTargetRotations[name],
                      axis
                    )
                  ) {
                    ready = false;
                  }
                });

                if (ready) {
                  // Ordre inversé : de gauche à droite
                  const sequence = [
                    { name: "piece_move_3", liquide: "liquide_1" },
                    { name: "piece_move_2", liquide: "liquide_3" },
                    { name: "piece_move_1", liquide: "liquide_2" },
                  ];

                  const playSequence = (index = 0) => {
                    if (index >= sequence.length) {
                      if (interactUIRef?.current) {
                        interactUIRef.current.exit();
                      }
                      CameraManager.resetLookTargetWithAnimation({
                        duration: 1,
                        onComplete: () => {
                          playAudioFile(
                            audios.fin_carburant,
                            subtitles.fin_carburant
                          );
                          setTimeout(() => {
                            CameraManager.playAnimation(() => {
                              nextStep();
                            }, 10000);
                          }, 5000);
                        },
                      });
                      return;
                    }
                    const { name, liquide } = sequence[index];
                    const piece = tuyau.getObjectByName(name);

                    const updaters = tuyauMap.current[liquide] || [];
                    const localFill = { value: 0 };
                    const tl = gsap.timeline();

                    const liquidAudio = new Audio(audios.liquide);
                    liquidAudio.play().catch(console.warn);

                    tl.to(localFill, {
                      value: 1,
                      duration: 2.6,
                      onUpdate: () => {
                        updaters.forEach(({ updateAlphaTexture, material }) => {
                          updateAlphaTexture(localFill.value);
                          material.color.set(pieceColorMap[name]);
                          material.emissive.set(pieceColorMap[name]);
                        });
                      },
                      onComplete: () => {
                        liquidAudio.addEventListener("ended", () => {
                          useFlowStore
                            .getState()
                            .playAudioFile(
                              `./audio/tuyau_rotate_${name}.mp3`,
                              `./audio/subtitles/tuyau_rotate_${name}.srt`,
                              () => {
                                playSequence(index + 1);
                              }
                            );
                        });
                      },
                    });
                  };

                  playSequence(0);
                } else {
                  playAudioFile(
                    audios.mathis_error,
                    subtitles.mathis_error
                  );
                  setTimeout(() => setLevierDisabled(false), 1200);
                }
              },
            });
          },
        });
      };
    }

    // ----------- TRAVERSE POUR LES PIECES, MANIVELLES, ENVELOPPES ET LIQUIDES -----------
    tuyau.visible = true;
    tuyau.frustumCulled = false;
    tuyau.traverse((child) => {
      if (!child.isMesh) return;
      child.raycast = THREE.Mesh.prototype.raycast;

      // Pièces : même traitement que dans le forEach au-dessus (sécurité si besoin)
      if (
        ["piece_move_1", "piece_move_2", "piece_move_3"].includes(child.name)
      ) {
        child.material = child.material.clone();
        child.material.isClonedForGlow = true;
        child.material.emissive = new THREE.Color(pieceColorMap[child.name]);
        child.material.emissiveIntensity = 1.5;
      }

      // Manivelles
      // if (Object.keys(manivelles).includes(child.name)) {
      //   child.material = child.material.clone();
      //   child.material.isClonedForGlow = true;
      //   // Couleur associée à la pièce
      //   let color = 0xffffff;
      //   Object.entries(pieceToManivelleMap).forEach(([pieceName, maniName]) => {
      //     if (maniName === child.name) {
      //       color = pieceColorMap[pieceName];
      //     }
      //   });
      //   child.material.emissive = new THREE.Color(color);
      //   child.material.emissiveIntensity = 1.5;
      // }

      // Enveloppe transparente du tuyau
      if (child.name.toLowerCase().startsWith("enveloppe")) {
        child.material = new THREE.MeshPhysicalMaterial({
          color: new THREE.Color(0xffffff),
          metalness: 0,
          roughness: 0.1,
          transmission: 1,
          thickness: 1,
          ior: 1.49,
          reflectivity: 0.5,
          clearcoat: 0.15,
          attenuationColor: new THREE.Color(0xffffff),
          attenuationDistance: 0.5,
          clearcoatRoughness: 0.1,
          transparent: true,
          opacity: 1,
          side: THREE.DoubleSide,
          depthWrite: false,
        });
        child.renderOrder = 2;
      }

      // Création de l'effet de liquide
      if (child.name.toLowerCase().startsWith("liquide")) {
        const canvas = document.createElement("canvas");
        const width = 1;
        const height = 1024;
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");

        const alphaMap = new THREE.CanvasTexture(canvas);
        alphaMap.wrapS = THREE.ClampToEdgeWrapping;
        alphaMap.wrapT = THREE.ClampToEdgeWrapping;
        alphaMap.minFilter = THREE.LinearFilter;
        alphaMap.magFilter = THREE.LinearFilter;

        const updateAlphaTexture = (progressRaw) => {
          const progress = Math.max(0, Math.min(1, Number(progressRaw) || 0));
          ctx.clearRect(0, 0, width, height);

          if (progress >= 1) {
            ctx.fillStyle = "white";
            ctx.fillRect(0, 0, width, height);
          } else {
            const fade = 0.02;
            // INVERSION : les progressions sont inversées ici
            const start = Math.min(1, progress);
            const end = Math.max(0, progress - fade);

            // Remarquez la direction du gradient inversée
            const grad = ctx.createLinearGradient(0, 0, 0, height);

            grad.addColorStop(0, "white"); // En haut
            grad.addColorStop(end, "white");
            grad.addColorStop(start, "black"); // En bas
            grad.addColorStop(1, "black");

            ctx.fillStyle = grad;
            ctx.fillRect(0, 0, width, height);
          }

          alphaMap.needsUpdate = true;
        };

        updateAlphaTexture(0);

        const material = new THREE.MeshPhysicalMaterial({
          color: new THREE.Color(0x00baff),
          metalness: 0,
          roughness: 0.5,
          transmission: 0,
          thickness: 10,
          reflectivity: 1,
          ior: 1.33,
          attenuationColor: new THREE.Color(0x00baff),
          attenuationDistance: 1.5,
          transparent: true,
          opacity: 0.6,
          side: THREE.DoubleSide,
          alphaMap: alphaMap,
        });

        child.renderOrder = 1;

        child.material = material;
        tuyauMap.current[child.name] = [{ updateAlphaTexture, material }];
        liquids.current.push({ updateAlphaTexture });
      }
    });
  }, [tuyau, levierDisabled, encouragementPlayed]);

  useIntersectingClicks(tuyau, (object, event) => {
    if (object.name === "hitbox_levier" && levierDisabled) return;
    object.userData.isClickable && object.userData.onClick?.();
  });

  if (!tuyau) return null;

  return <primitive object={tuyau} />;
}
